import React, { useState } from 'react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import OverrideTable from '@/components/overrides/OverrideTable'
import OverrideForm from '@/components/overrides/OverrideForm'
import { Button } from '@/components/ui/button'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import { Plus, FileEdit } from 'lucide-react'
import type { SharedProps } from '@/types/shared'
import type { Override } from '@/components/overrides/OverrideTable'

interface RoomTypeOption {
  id: number
  name: string
  propertyId?: number | null
  propertyName?: string | null
}

interface RatePlanOption {
  id: number
  name: string
  propertyId?: number | null
  propertyName?: string | null
}

interface OverridesProps extends SharedProps {
  overrides: Override[]
  roomTypes: RoomTypeOption[]
  ratePlans: RatePlanOption[]
}

export default function Overrides({
  currentUser,
  property,
  properties,
  overrides,
  roomTypes,
  ratePlans,
  reportingScope,
}: OverridesProps) {
  const [formOpen, setFormOpen] = useState(false)

  return (
    <OperatorLayout title="Overrides" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <div className="animate-slide-in flex items-center justify-between" style={{ animationDelay: '0ms' }}>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Rate Overrides
              </h1>
              <HelpTooltip
                content="Overrides let you manually set a fixed rate or booking restriction for specific dates. They bypass the entire rate engine — no seasons, demand, or other rules apply. Use them for one-off situations like maintenance closures, group bookings, or special pricing."
                learnMore="/guides/overrides"
              />
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Manually adjust rates and restrictions for specific dates.
            </p>
          </div>
          <Button onClick={() => setFormOpen(true)} className="rounded-lg">
            <Plus className="mr-2 h-4 w-4" />
            New Override
          </Button>
        </div>

        {/* Overrides are made at the lead for every hotel in the unit, and each one still publishes
            under its own hotel's shortname. Say both — the second half is what stops "managed here"
            being read as "merged". */}
        <UnitScopeNote
          scope={reportingScope}
          covers="Every override listed and created here"
          listHotels
          className="animate-slide-in"
        />

        <div className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          {overrides.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <FileEdit className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No active overrides</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Create one to manually adjust rates or restrictions.
              </p>
              <Button className="mt-4 rounded-lg" onClick={() => setFormOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Override
              </Button>
            </div>
          ) : (
            <OverrideTable
              overrides={overrides}
              propertyBasePath={property.basePath}
              currency={property.currency}
              leadPropertyId={reportingScope?.leadPropertyId ?? null}
            />
          )}
        </div>

        <OverrideForm
          propertyBasePath={property.basePath}
          roomTypes={roomTypes}
          ratePlans={ratePlans}
          open={formOpen}
          onOpenChange={setFormOpen}
          reportingScope={reportingScope}
        />
      </div>
    </OperatorLayout>
  )
}
